/**
 * CommissionedHeaderMobile — redesigned mobile header for COMMISSIONED workers.
 *
 * Same family as the hourly header, but the dial is an EARNINGS meter: today's
 * pay $0 → daily target, red→amber→green gradient. Beside it: TODAY and WEEK
 * totals with job counts. Ported from the Claude-Design handoff (Turn 1).
 */

import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { SignOut, UserCircle, Sun, CalendarBlank } from '@phosphor-icons/react';
import NotificationBell from './NotificationBell';

const fmtMoney = (n) => '$' + (Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Point on the r=70 semicircle at a given fraction (0..1) of the 180° sweep.
function pctToXY(pct) {
  const th = Math.max(0, Math.min(1, pct)) * Math.PI;
  return { x: 90 - 70 * Math.cos(th), y: 100 - 70 * Math.sin(th) };
}

export default function CommissionedHeaderMobile({ user, onLogout, onAccount }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.getMyEarnings().then(setData).catch(() => {});
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, []);

  const todayTotal = data ? data.today.total : 0;
  const todayJobs  = data ? data.today.jobs : 0;
  const weekTotal  = data ? data.week.total : 0;
  const weekJobs   = data ? data.week.jobs : 0;
  const target     = data && data.dailyTarget > 0 ? data.dailyTarget : 300;

  const pct  = Math.max(0, Math.min(1, target > 0 ? todayTotal / target : 0));
  const full = pct >= 1;
  const tip  = pctToXY(pct);
  const arcD = `M20,100 A70,70 0 0 1 ${tip.x.toFixed(1)},${tip.y.toFixed(1)}`;
  const amountColor = full ? '#16a34a' : '#c2410c';

  const iconBtn = { position: 'relative', background: '#f3f4f6', border: 'none', width: 38, height: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };

  return (
    <header className="mobile-comm-header" style={{ background: '#fff', padding: '6px 16px 16px', borderBottom: '1px solid #eef0f2', position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <img src={process.env.PUBLIC_URL + '/ACTS_CAR_DETAILING.png'} alt="Acts" style={{ height: 46, objectFit: 'contain' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NotificationBell onAccount={onAccount} />
          <button onClick={onAccount} style={{ ...iconBtn, color: '#1e3a4a' }} title="My Account" aria-label="My Account">
            <UserCircle size={20} weight="duotone" />
          </button>
          <button onClick={onLogout} style={{ ...iconBtn, color: '#374151' }} title="Logout" aria-label="Logout">
            <SignOut size={19} />
          </button>
        </div>
      </div>

      {/* Earnings dial + side stats */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <svg width="180" height="112" viewBox="0 0 180 112" style={{ flex: 'none' }}>
          <defs>
            <linearGradient id="earnGrad" gradientUnits="userSpaceOnUse" x1="20" y1="0" x2="160" y2="0">
              <stop offset="0" stopColor="#e01818" />
              <stop offset="0.55" stopColor="#f59e0b" />
              <stop offset="1" stopColor="#16a34a" />
            </linearGradient>
          </defs>
          {/* track + tick ring */}
          <path d="M20,100 A70,70 0 0 1 160,100" fill="none" stroke="#eceef0" strokeWidth="16" />
          <path d="M20,100 A70,70 0 0 1 160,100" fill="none" stroke="#f8f9fa" strokeWidth="10" strokeDasharray="1 3.6" />
          {/* fill arc */}
          {pct > 0.001 && <>
            <path d={arcD} fill="none" stroke="url(#earnGrad)" strokeWidth="16" strokeLinecap="round" />
            <path d={arcD} fill="none" stroke="rgba(255,255,255,.35)" strokeWidth="11" strokeDasharray="1.4 4" />
          </>}
          {/* progress dot (partial only) */}
          {!full && pct > 0.001 && (
            <g transform={`translate(${tip.x.toFixed(1)},${tip.y.toFixed(1)})`} style={{ animation: 'earnPulse 1.8s ease-in-out infinite' }}>
              <circle r="6.5" fill="#fff" /><circle r="4" fill="#e8590c" />
            </g>
          )}
          {/* end knobs */}
          <circle cx="20" cy="100" r="8" fill="#e01818" /><circle cx="20" cy="100" r="2.6" fill="#fff" />
          {full ? (
            <g transform="translate(160,100)">
              <circle r="9" fill="#16a34a" />
              <path d="M-3.5,0 L-1,2.6 L3.8,-2.6" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          ) : (
            <><circle cx="160" cy="100" r="8" fill="#16a34a" /><circle cx="160" cy="100" r="2.6" fill="#fff" /></>
          )}
          {/* centre */}
          <text x="90" y="82" textAnchor="middle" style={{ font: '700 7px system-ui', letterSpacing: '1px', fill: '#9ca3af' }}>EARNED TODAY</text>
          <text x="90" y="100" textAnchor="middle" style={{ font: '600 19px system-ui', letterSpacing: '-.5px', fill: amountColor, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(todayTotal)}</text>
        </svg>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 18 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, font: '700 9px system-ui', letterSpacing: '.6px', color: '#9ca3af' }}>
                <Sun size={12} weight="fill" color="#f59e0b" />TODAY
              </div>
              <div style={{ font: '700 19px system-ui', color: full ? '#16a34a' : '#1a1a2e', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(todayTotal)}</div>
              <div style={{ font: '400 11px system-ui', color: '#9ca3af' }}>{todayJobs} job{todayJobs === 1 ? '' : 's'}</div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, font: '700 9px system-ui', letterSpacing: '.6px', color: '#9ca3af' }}>
                <CalendarBlank size={12} weight="fill" color="#4A7A91" />WEEK
              </div>
              <div style={{ font: '700 19px system-ui', color: '#1a1a2e', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(weekTotal)}</div>
              <div style={{ font: '400 11px system-ui', color: '#9ca3af' }}>{weekJobs} job{weekJobs === 1 ? '' : 's'}</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes earnPulse { 0%,100% { opacity: 1 } 50% { opacity: .55 } }`}</style>
    </header>
  );
}
